/* eslint-disable @cspell/spellchecker */
import { cn } from '@exam-room/utils';

export enum IconName {
  AccessTime = 'access_time',
  Add = 'add',
  ArrowBack = 'arrow_back',
  ArrowForward = 'arrow_forward',
  AutoAwesome = 'auto_awesome',
  Bookmark = 'bookmark',
  BookmarkBorder = 'bookmark_border',
  Calculate = 'calculate',
  Cancel = 'cancel',
  Chat = 'chat',
  Check = 'check',
  CheckCircle = 'check_circle',
  CheckCircleOutline = 'check_circle_outline',
  Clear = 'clear',
  Close = 'close',
  DeleteOutline = 'delete_outline',
  DesignServices = 'design_services',
  Done = 'done',
  Error = 'error',
  ErrorOutline = 'error_outline',
  ExpandMore = 'expand_more',
  FactCheck = 'fact_check',
  Feed = 'feed',
  FormatBold = 'format_bold',
  FormatItalic = 'format_italic',
  FormatUnderlined = 'format_underlined',
  FormatAlignLeft = 'format_align_left',
  FormatAlignCenter = 'format_align_center',
  FormatAlignRight = 'format_align_right',
  FormatListBulleted = 'format_list_bulleted',
  FormatListNumbered = 'format_list_numbered',
  Functions = 'functions',
  Subscript = 'subscript',
  Superscript = 'superscript',
  HelpOutline = 'help_outline',
  Image = 'image',
  InsertDriveFile = 'insert_drive_file',
  Info = 'info',
  KeyboardArrowDown = 'keyboard_arrow_down',
  KeyboardArrowLeft = 'keyboard_arrow_left',
  KeyboardArrowRight = 'keyboard_arrow_right',
  KeyboardArrowUp = 'keyboard_arrow_up',
  Logout = 'logout',
  Menu = 'menu',
  MenuBook = 'menu_book',
  MoreVert = 'more_vert',
  OpenInNew = 'open_in_new',
  Person = 'person',
  Remove = 'remove',
  SentimentDissatisfied = 'sentiment_dissatisfied',
  SentimentSatisfiedAlt = 'sentiment_satisfied_alt',
  TableChart = 'table_chart',
  VideoCam = 'videocam',
  ViewList = 'view_list',
  Visibility = 'visibility',
  VisibilityOff = 'visibility_off',
  Warning = 'warning',
  WatchLater = 'watch_later',
  WifiOff = 'wifi_off',
  UploadFile = 'upload_file',
}

interface IconProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: IconName;
  className?: string;
  variant?: 'outlined' | 'rounded';
}

export function Icon({ icon, className, variant = 'outlined', ...props }: IconProps) {
  return (
    <div className={cn('text-[24px] !leading-0', className)} {...props}>
      <i
        className={cn(variant === 'outlined' ? 'material-icons-outlined' : 'material-icons-round')}
      >
        {icon}
      </i>
    </div>
  );
}

function createIconComponent(icon: IconName, variant?: 'outlined' | 'rounded') {
  return function IconComponent(props: Omit<IconProps, 'icon'>) {
    return <Icon icon={icon} {...props} variant={variant} />;
  };
}

// Outlined Icons
export const AddOutlinedIcon = createIconComponent(IconName.Add);
export const AutoAwesomeOutlinedIcon = createIconComponent(IconName.AutoAwesome);
export const BookmarkBorderOutlinedIcon = createIconComponent(IconName.BookmarkBorder);
export const BookmarkIcon = createIconComponent(IconName.Bookmark);
export const CalculateOutlinedIcon = createIconComponent(IconName.Calculate);
export const CancelOutlinedIcon = createIconComponent(IconName.Cancel);
export const ChatOutlinedIcon = createIconComponent(IconName.Chat);
export const CheckOutlinedIcon = createIconComponent(IconName.Check);
export const CheckCircleOutlinedIcon = createIconComponent(IconName.CheckCircleOutline);
export const ClearOutlinedIcon = createIconComponent(IconName.Clear);
export const DeleteOutlineIcon = createIconComponent(IconName.DeleteOutline);
export const DesignServicesOutlinedIcon = createIconComponent(IconName.DesignServices);
export const ErrorOutlineIcon = createIconComponent(IconName.ErrorOutline);
export const ExpandMoreOutlinedIcon = createIconComponent(IconName.ExpandMore);
export const FactCheckOutlinedIcon = createIconComponent(IconName.FactCheck);
export const FeedOutlinedIcon = createIconComponent(IconName.Feed);
export const HelpOutlineIcon = createIconComponent(IconName.HelpOutline);
export const InsertDriveFileOutlinedIcon = createIconComponent(IconName.InsertDriveFile);
export const MenuIconOutlined = createIconComponent(IconName.Menu);
export const MenuBookOutlinedIcon = createIconComponent(IconName.MenuBook);
export const MoreVertOutlinedIcon = createIconComponent(IconName.MoreVert);
export const PersonOutlinedIcon = createIconComponent(IconName.Person);
export const RemoveOutlinedIcon = createIconComponent(IconName.Remove);
export const SentimentDissatisfiedOutlinedIcon = createIconComponent(
  IconName.SentimentDissatisfied,
);
export const SentimentSatisfiedAltOutlinedIcon = createIconComponent(
  IconName.SentimentSatisfiedAlt,
);
export const VideoCamOutlinedIcon = createIconComponent(IconName.VideoCam);
export const VisibilityOutlinedIcon = createIconComponent(IconName.Visibility);
export const VisibilityOffOutlinedIcon = createIconComponent(IconName.VisibilityOff);
export const WatchLaterOutlinedIcon = createIconComponent(IconName.WatchLater);
export const FormatBoldOutlinedIcon = createIconComponent(IconName.FormatBold);
export const FormatItalicOutlinedIcon = createIconComponent(IconName.FormatItalic);
export const FormatUnderlinedOutlinedIcon = createIconComponent(IconName.FormatUnderlined);
export const FormatListBulletedOutlinedIcon = createIconComponent(IconName.FormatListBulleted);
export const FormatListNumberedOutlinedIcon = createIconComponent(IconName.FormatListNumbered);
export const FormatAlignLeftOutlinedIcon = createIconComponent(IconName.FormatAlignLeft);
export const FormatAlignCenterOutlinedIcon = createIconComponent(IconName.FormatAlignCenter);
export const FormatAlignRightOutlinedIcon = createIconComponent(IconName.FormatAlignRight);
export const ImageOutlinedIcon = createIconComponent(IconName.Image);
export const TableChartOutlinedIcon = createIconComponent(IconName.TableChart);
export const FunctionsOutlinedIcon = createIconComponent(IconName.Functions);
export const SubscriptOutlinedIcon = createIconComponent(IconName.Subscript);
export const SuperscriptOutlinedIcon = createIconComponent(IconName.Superscript);

// Rounded Icons
export const ArrowBackRoundedIcon = createIconComponent(IconName.ArrowBack, 'rounded');
export const ArrowForwardRoundedIcon = createIconComponent(IconName.ArrowForward, 'rounded');
export const CheckCircleRoundedIcon = createIconComponent(IconName.CheckCircle, 'rounded');
export const CloseRoundedIcon = createIconComponent(IconName.Close, 'rounded');
export const InfoRoundedIcon = createIconComponent(IconName.Info, 'rounded');
export const KeyboardArrowDownRoundedIcon = createIconComponent(
  IconName.KeyboardArrowDown,
  'rounded',
);
export const KeyboardArrowLeftRoundedIcon = createIconComponent(
  IconName.KeyboardArrowLeft,
  'rounded',
);
export const KeyboardArrowRightRoundedIcon = createIconComponent(
  IconName.KeyboardArrowRight,
  'rounded',
);
export const KeyboardArrowUpRoundedIcon = createIconComponent(IconName.KeyboardArrowUp, 'rounded');
export const LogoutRoundedIcon = createIconComponent(IconName.Logout, 'rounded');
export const OpenInNewRoundedIcon = createIconComponent(IconName.OpenInNew, 'rounded');
export const AccessTimeRoundedIcon = createIconComponent(IconName.AccessTime, 'rounded');
export const WarningRoundedIcon = createIconComponent(IconName.Warning, 'rounded');
export const ErrorRoundedIcon = createIconComponent(IconName.Error, 'rounded');
export const FormatBoldRoundedIcon = createIconComponent(IconName.FormatBold, 'rounded');
export const FormatItalicRoundedIcon = createIconComponent(IconName.FormatItalic, 'rounded');
export const FormatUnderlinedRoundedIcon = createIconComponent(
  IconName.FormatUnderlined,
  'rounded',
);
export const FormatAlignLeftRoundedIcon = createIconComponent(IconName.FormatAlignLeft, 'rounded');
export const FormatAlignCenterRoundedIcon = createIconComponent(
  IconName.FormatAlignCenter,
  'rounded',
);
export const FormatAlignRightRoundedIcon = createIconComponent(
  IconName.FormatAlignRight,
  'rounded',
);
export const FormatListBulletedRoundedIcon = createIconComponent(
  IconName.FormatListBulleted,
  'rounded',
);
export const FormatListNumberedRoundedIcon = createIconComponent(
  IconName.FormatListNumbered,
  'rounded',
);
export const ImageRoundedIcon = createIconComponent(IconName.Image, 'rounded');
export const ViewListRoundedIcon = createIconComponent(IconName.ViewList, 'rounded');
export const TableChartRoundedIcon = createIconComponent(IconName.TableChart, 'rounded');
export const FunctionsRoundedIcon = createIconComponent(IconName.Functions, 'rounded');
export const DoneIcon = createIconComponent(IconName.Done, 'outlined');
export const WifiOffIcon = createIconComponent(IconName.WifiOff, 'outlined');
export const UploadFileIcon = createIconComponent(IconName.UploadFile, 'outlined');

// Custom SVG Icons
export function LatexIcon({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('text-[24px] !leading-0', className)} {...props}>
      <svg
        xmlns='http://www.w3.org/2000/svg'
        width='24'
        height='24'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
        <path d='M19 5h-7l-4 14l-3 -6h-2' />
        <path d='M14 13l6 6' />
        <path d='M14 19l6 -6' />
      </svg>
    </div>
  );
}

export function CustomWarningOutlineIcon({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('text-[24px] !leading-0', className)} {...props}>
      <svg
        width='98'
        height='86'
        viewBox='0 0 98 86'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M48.6911 25.955C51.04 25.955 52.9443 27.8592 52.9443 30.2082V47.2208C52.9443 49.5697 51.04 51.4739 48.6911 51.4739C46.3421 51.4739 44.4379 49.5697 44.4379 47.2208V30.2082C44.4379 27.8592 46.3421 25.955 48.6911 25.955Z'
          fill='#F59F00'
        />
        <path
          d='M44.4379 64.2334C44.4379 61.8844 46.3421 59.9802 48.6911 59.9802H48.7336C51.0826 59.9802 52.9868 61.8844 52.9868 64.2334C52.9868 66.5823 51.0826 68.4865 48.7336 68.4865H48.6911C46.3421 68.4865 44.4379 66.5823 44.4379 64.2334Z'
          fill='#F59F00'
        />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M48.6901 0C46.4972 0 44.3413 0.565192 42.4304 1.64104C40.5195 2.71689 38.9181 4.26708 37.7808 6.14204L37.7686 6.16227L1.74444 66.3018L1.70989 66.3606C0.595785 68.2899 0.00628834 70.4774 5.00282e-05 72.7053C-0.00618803 74.9333 0.571048 77.124 1.67433 79.0596C2.77761 80.9952 4.36848 82.6081 6.28867 83.738C8.20884 84.8679 10.3914 85.4753 12.6192 85.4997L12.666 85.5H84.7143L84.7611 85.4997C86.9889 85.4753 89.1715 84.8679 91.0916 83.738C93.0118 82.6081 94.6027 80.9952 95.706 79.0596C96.8092 77.124 97.3865 74.9333 97.3802 72.7053C97.374 70.4774 96.7845 68.2899 95.6704 66.3606L95.6359 66.3018L59.6117 6.16227L59.5995 6.14204C58.4622 4.26708 56.8608 2.71689 54.9499 1.64104C53.039 0.565192 50.8831 0 48.6901 0ZM46.6036 9.05332C47.2405 8.6947 47.9592 8.5063 48.6901 8.5063C49.4211 8.5063 50.1398 8.6947 50.7767 9.05332C51.4108 9.41031 51.9427 9.924 52.3214 10.5452L52.3266 10.5536L88.3182 70.639C88.6804 71.276 88.8719 71.996 88.874 72.7291C88.8761 73.4718 88.6836 74.202 88.3159 74.8472C87.9481 75.4924 87.4178 76.0301 86.7778 76.4067C86.1433 76.78 85.4229 76.9822 84.6871 76.9937H12.6932C11.9574 76.9822 11.237 76.78 10.6025 76.4067C9.96246 76.0301 9.43217 75.4924 9.06441 74.8472C8.69665 74.202 8.50424 73.4718 8.50632 72.7291C8.50837 71.9959 8.69994 71.2759 9.06221 70.6387L45.0589 10.5452C45.4376 9.924 45.9695 9.41031 46.6036 9.05332Z'
          fill='#F59F00'
        />
      </svg>
    </div>
  );
}
